import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import config from 'config';

const BackendStatusContext = createContext();

// Constants
const INITIAL_CHECK_TIMEOUT = 8000; // 8 seconds timeout for initial check
const MAX_CONSECUTIVE_FAILURES = 3; // Number of consecutive failures before showing error

export const useBackendStatus = () => {
  const context = useContext(BackendStatusContext);
  if (!context) {
    throw new Error('useBackendStatus must be used within a BackendStatusProvider');
  }
  return context;
};

export const BackendStatusProvider = ({ children }) => {
  const [isBackendAvailable, setIsBackendAvailable] = useState(true);
  const [isChecking, setIsChecking] = useState(true);
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false);
  const consecutiveFailures = useRef(0);

  // Check backend availability
  const checkBackend = useCallback(async () => {
    try {
      await axios.get(`${config.apiUrl}/health`, {
        timeout: INITIAL_CHECK_TIMEOUT,
      });
      
      // Backend is available
      consecutiveFailures.current = 0;
      setIsBackendAvailable(true);
      return true;
    } catch (error) {
      // IMPORTANT: Ignore cancelled requests - they are NOT network errors
      if (axios.isCancel(error)) {
        return isBackendAvailable; // Keep current state, don't treat as failure
      }
      
      // Check if it's a network error (no response from server)
      if (!error.response) {
        consecutiveFailures.current += 1;
        
        if (consecutiveFailures.current >= MAX_CONSECUTIVE_FAILURES) {
          setIsBackendAvailable(false);
        }
        return false;
      } else {
        // Server responded (even with an error like 404, 500), backend is alive
        consecutiveFailures.current = 0;
        setIsBackendAvailable(true);
        return true;
      }
    }
  }, [isBackendAvailable]);

  // Initial check on mount
  useEffect(() => {
    const initialCheck = async () => {
      setIsChecking(true);
      await checkBackend();
      setIsChecking(false);
      setHasCheckedOnce(true);
    };

    initialCheck();
  }, [checkBackend]);

  // Set up global axios interceptor to detect backend going down
  // Use a ref to avoid re-registering the interceptor on state changes
  const isBackendAvailableRef = useRef(isBackendAvailable);
  useEffect(() => {
    isBackendAvailableRef.current = isBackendAvailable;
  }, [isBackendAvailable]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        // Successful response - backend is available
        if (!isBackendAvailableRef.current) {
          consecutiveFailures.current = 0;
          setIsBackendAvailable(true);
        }
        return response;
      },
      (error) => {
        // IMPORTANT: Ignore cancelled requests - they are NOT network errors
        // This happens when component unmounts and cancels pending requests
        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }
        
        // Check if it's a network error (no response from server)
        if (!error.response) {
          consecutiveFailures.current += 1;
          
          if (consecutiveFailures.current >= MAX_CONSECUTIVE_FAILURES) {
            setIsBackendAvailable(false);
          }
        } else {
          // Server responded, backend is alive (even 4xx/5xx errors mean server is running)
          consecutiveFailures.current = 0;
          if (!isBackendAvailableRef.current) {
            setIsBackendAvailable(true);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []); // Register once, use ref for current state

  // Manual retry function
  const retryConnection = useCallback(async () => {
    setIsChecking(true);
    consecutiveFailures.current = 0;
    const result = await checkBackend();
    setIsChecking(false);
    
    if (result) {
      // Backend is back, reload the page to restore the app state
      window.location.reload();
    }
    
    return result;
  }, [checkBackend]);

  const value = {
    isBackendAvailable,
    isChecking,
    hasCheckedOnce,
    retryConnection,
    checkBackend,
  };

  return (
    <BackendStatusContext.Provider value={value}>
      {children}
    </BackendStatusContext.Provider>
  );
};

export default BackendStatusContext;
